import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  useFormContext,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import { Condition } from './Condition';
import { ConditionPreview } from './ConditionPreview';
import type { Question } from '@aphp-ema/common/questionnaire';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';

interface DisplayConditionsTabProps {
  questionIndex: number;
  previousQuestions: Question[];
}

export const ConditionsList: React.FC<DisplayConditionsTabProps> = ({
  questionIndex,
  previousQuestions,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<QuestionnaireFormData>();

  const conditionsPath =
    `questions.${questionIndex}.displayConditions` as const;

  const {
    fields: conditions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `${conditionsPath}.conditions`,
  });

  const addCondition = () => {
    append({
      id: Math.random().toString(36).substring(7),
      target: { source: 'question', questionId: '', questionLabel: '' },
      operator: 'equals',
      value: '',
    });
  };

  const logicalOperator = useWatch({
    control,
    name: `${conditionsPath}.logicalOperator`,
    defaultValue: 'AND',
  });

  useEffect(() => {
    if (logicalOperator === null) {
      setValue(`${conditionsPath}.logicalOperator`, 'AND');
    }
  }, [logicalOperator, setValue, conditionsPath]);

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('pages.studies.questions.conditions.whenToShow')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box
          sx={{
            display: conditions.length > 1 ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: '120px',
          }}
        >
          <Typography variant="caption" sx={{ mb: 1, textAlign: 'center' }}>
            {t('pages.studies.questions.conditions.combineConditionsWith')}
          </Typography>
          <Controller
            control={control}
            name={`${conditionsPath}.logicalOperator`}
            render={({ field }) => (
              <ToggleButtonGroup
                value={field.value}
                exclusive
                onChange={(_, value) => value && field.onChange(value)}
                size="small"
              >
                <ToggleButton value="AND" sx={{ px: 2 }}>
                  {t('pages.studies.questions.conditions.and')}
                </ToggleButton>
                <ToggleButton value="OR" sx={{ px: 2 }}>
                  {t('pages.studies.questions.conditions.or')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {conditions.map((condition, index) => (
            <Condition
              key={condition.id}
              questionIndex={questionIndex}
              conditionIndex={index}
              onRemove={() => remove(index)}
            />
          ))}
        </Box>
      </Box>

      <Button
        startIcon={<AddIcon />}
        onClick={addCondition}
        variant="outlined"
        sx={{ mt: 1 }}
      >
        {t('pages.studies.questions.conditions.addCondition')}
      </Button>

      {conditions.length > 0 && (
        <ConditionPreview
          questionIndex={questionIndex}
          previousQuestions={previousQuestions}
        />
      )}
    </Box>
  );
};
